import { useMemo } from "react";
import { useRentals, type RentalListing } from "@/hooks/use-rentals";

/**
 * Listings eligible for adding a lead/application.
 *
 * Rules:
 *   - The listing must exist and be currently **active** (live).
 *   - A listing that exists but isn't active (draft / paused / archived) is
 *     excluded.
 *   - A listing that is on rent (`status === "rented"`) is excluded too — it's
 *     no longer taking applications. (Covered by the active-only check, since
 *     "rented" is a distinct status from "active".)
 *
 * Note: deliberately NOT gated on `portfolio` membership — a listable property
 * has a completed rehab project, which `usePortfolio` strips out, so gating on
 * it would wrongly hide every listing.
 */
export function filterListingsForAddLead(
  listings: RentalListing[],
): RentalListing[] {
  return listings.filter((l) => l.status === "active");
}

export function useListingsForAddLead() {
  const { listings, isLoading } = useRentals();

  const listingsForAddLead = useMemo(
    () => filterListingsForAddLead(listings),
    [listings],
  );

  return {
    listingsForAddLead,
    isLoading,
  };
}
